import { useEffect, useRef, useState } from "react";
import type { U2000FormStateHandlers } from "../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useU3125CustomerCreditBalanceHandlers } from "../../../U3125UseApiHandlers";
import type { U3125CreditSource, U3125InvoiceFormRow } from "../../../U3125Service";
import type { U3125FactureNestedParentHandlers } from "./u3125FactureInvoiceLinesTab/U3125InvoiceLineHandlers";

function creditSourcesEqual(a: U3125CreditSource[] | undefined, b: U3125CreditSource[]): boolean {
  const left = a ?? [];
  if (left.length !== b.length) return false;
  return left.every((item, i) => item.invoiceId === b[i]?.invoiceId && item.remainingAmount === b[i]?.remainingAmount);
}

export function useU3125CreditApplication(
  parentFormHandlers: U3125FactureNestedParentHandlers & Pick<U2000FormStateHandlers<U3125InvoiceFormRow>, "setData">,
  _readonly: boolean,
) {
  const parentRef = useRef(parentFormHandlers);
  parentRef.current = parentFormHandlers;

  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);

  const { fetchCustomerCreditBalance } = useU3125CustomerCreditBalanceHandlers();

  const data = parentFormHandlers.getData();
  const customerId = data?.customer?.id?.trim();
  const documentNature = data?.documentNature ?? "STANDARD";
  /** Recharger les soldes sources après save (version facture) — pas à chaque edit local. */
  const invoiceVersion = data?.version ?? 0;

  useEffect(() => {
    if (!customerId || documentNature !== "STANDARD") return;

    let cancelled = false;
    if (!loadingRef.current) {
      loadingRef.current = true;
      setLoading(true);
    }

    void (async () => {
      try {
        const balance = await fetchCustomerCreditBalance(customerId);
        if (cancelled) return;
        const nextSources = balance?.availableSources ?? [];
        const row = parentRef.current.getData();
        if (!row || creditSourcesEqual(row.availableCreditSources, nextSources)) return;
        parentRef.current.setData({
          ...row,
          availableCreditSources: nextSources,
        });
      } catch {
        if (cancelled) return;
        const row = parentRef.current.getData();
        if (!row || (row.availableCreditSources?.length ?? 0) === 0) return;
        parentRef.current.setData({ ...row, availableCreditSources: [] });
      } finally {
        if (!cancelled) {
          loadingRef.current = false;
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [customerId, documentNature, invoiceVersion, fetchCustomerCreditBalance]);

  return { loading, sources: data?.availableCreditSources ?? [] };
}
